import { useEffect, useState } from "react";
import api, { withSite } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import PageHeader from "@/components/PageHeader";
import PdfButton from "@/components/PdfButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Truck } from "lucide-react";

const RATING_STYLES = {
  A: "bg-eucalyptus-900 text-white",
  B: "bg-eucalyptus-500 text-white",
  C: "bg-[#D69F3C] text-white",
  D: "bg-ochre-600 text-white",
  "—": "bg-bone-alt text-charcoal-soft",
};

export default function SupplierScorecard() {
  const { activeSite } = useSite();
  const [days, setDays] = useState(90);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeSite) return;
    setLoading(true);
    api
      .get("/suppliers/scorecard", withSite(activeSite.id, { days }))
      .then(({ data }) => setRows(data.suppliers))
      .finally(() => setLoading(false));
  }, [activeSite, days]);

  return (
    <div className="animate-fade-in" data-testid="scorecard-page">
      <PageHeader
        title="Supplier Scorecard"
        subtitle="Compliance ratings derived from your receival logs: acceptance rate, delivery temperature compliance and delivery volume. Use at the next procurement negotiation."
        action={
          <div className="flex items-center gap-3">
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
              <SelectTrigger data-testid="sc-range" className="h-11 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <PdfButton kind="receival" label="Receival PDF" />
          </div>
        }
      />

      <div className="surface overflow-hidden">
        <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
          <div className="col-span-1 text-center">Rating</div>
          <div className="col-span-3">Supplier</div>
          <div className="col-span-1 text-center">Score</div>
          <div className="col-span-1 text-center">Deliveries</div>
          <div className="col-span-2 text-center">Acceptance</div>
          <div className="col-span-2 text-center">Temp compliance</div>
          <div className="col-span-2">Last delivery</div>
        </div>
        {loading && <div className="p-10 text-center text-charcoal-soft text-sm">Calculating…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-charcoal-soft text-sm">
            No suppliers yet. Add some in Settings to start scoring.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.supplier_id}
            data-testid={`sc-row-${r.supplier_id}`}
            className="grid grid-cols-12 px-6 py-4 border-t border-[color:var(--border)] items-center"
          >
            <div className="col-span-1 text-center">
              <span
                className={`inline-flex w-10 h-10 rounded-md items-center justify-center font-display font-extrabold ${RATING_STYLES[r.rating] || RATING_STYLES["—"]}`}
              >
                {r.rating}
              </span>
            </div>
            <div className="col-span-3">
              <div className="font-medium flex items-center gap-2">
                <Truck className="w-4 h-4 text-eucalyptus-700" /> {r.supplier_name}
              </div>
              <div className="text-xs text-charcoal-soft font-mono">{r.abn}</div>
            </div>
            <div className="col-span-1 text-center font-mono font-bold">
              {r.score ?? "—"}
            </div>
            <div className="col-span-1 text-center">
              <Badge variant="outline" className="font-mono">
                {r.deliveries}
              </Badge>
            </div>
            <div className="col-span-2 text-center">
              {r.acceptance_pct !== null ? (
                <div className="inline-flex items-center gap-2">
                  <div className="w-24 h-2 bg-bone-alt rounded-full overflow-hidden">
                    <div
                      className="h-full bg-eucalyptus-700"
                      style={{ width: `${r.acceptance_pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs">{r.acceptance_pct}%</span>
                </div>
              ) : (
                <span className="text-charcoal-soft text-xs">—</span>
              )}
            </div>
            <div className="col-span-2 text-center">
              {r.temp_compliant_pct !== null ? (
                <div className="inline-flex items-center gap-2">
                  <div className="w-24 h-2 bg-bone-alt rounded-full overflow-hidden">
                    <div
                      className={`h-full ${r.temp_compliant_pct >= 90 ? "bg-eucalyptus-700" : "bg-ochre-600"}`}
                      style={{ width: `${r.temp_compliant_pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs">{r.temp_compliant_pct}%</span>
                </div>
              ) : (
                <span className="text-charcoal-soft text-xs">—</span>
              )}
            </div>
            <div className="col-span-2 text-xs">
              {r.last_delivery ? (
                <>
                  <div className="font-mono text-charcoal-soft">
                    {new Date(r.last_delivery).toLocaleDateString("en-AU")}
                  </div>
                  <div className="truncate">{r.last_product}</div>
                </>
              ) : (
                <span className="text-charcoal-soft">No deliveries</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 surface-alt p-5 text-xs text-charcoal-soft">
        <div className="flex items-center gap-2 mb-2 label-eyebrow">
          <TrendingUp className="w-4 h-4" /> How scoring works
        </div>
        <div className="font-mono">
          score = 0.6 × acceptance% + 0.4 × delivery-temp-compliance%. Rating:
          A ≥ 90, B ≥ 75, C ≥ 60, else D.
        </div>
      </div>
    </div>
  );
}
